import { InMemoryCache } from "@apollo/client";

/**
 * Функция мерджа списков в кэше для запросов у которых пагинация реализована через skip, limit
 */
// Функция для извлечения ID из объекта или нормализованной ссылки
const getNodeId = (node) => {
  if (!node) return null;
  // Если это нормализованная ссылка Apollo Client
  if (node.__ref) {
    return node.__ref.split(":")[1]; // "Content:actualId" -> "actualId"
  }
  // Если это полный объект
  return node.id;
};

// Функция для получения ID с обработкой нормализованных ссылок
const getExistingIds = (nodes) => nodes.map(getNodeId).filter(Boolean);

const connectionLimitMerge = (existing, incoming, { args: { skip = 0 } }) => {
  const { count, __typename } = incoming;

  // Если это первая страница (skip = 0)
  if (skip === 0) {
    // Если у нас уже есть больше данных чем приходит, не перезаписываем
    if (existing && existing.nodes.length > incoming.nodes.length) {
      return existing;
    }
    return {
      __typename,
      count,
      nodes: incoming.nodes,
    };
  }

  // Если это загрузка следующей страницы (skip > 0)
  if (existing) {
    // Создаем Set существующих ID для быстрой проверки
    const existingIds = new Set(getExistingIds(existing.nodes));

    // Фильтруем только новые элементы, которых еще нет в кеше
    const newNodes = incoming.nodes.filter((node) => {
      const nodeId = getNodeId(node);
      return nodeId && !existingIds.has(nodeId);
    });

    return {
      __typename,
      count,
      nodes: [...existing.nodes, ...newNodes],
    };
  }

  // Если нет существующих данных, просто возвращаем новые
  return {
    __typename,
    count,
    nodes: incoming.nodes,
  };
};

/**
 * Функция мерджа списков в кэше для запросов у которых пагинация реализована через page, rowsPerPage
 */
const connectionPageMerge = (existing, incoming, { args: { page = 0 } }) => {
  const { count, __typename } = incoming;

  // Если это первая страница (page = 0), заменяем кеш
  if (page === 0) {
    // Если у нас уже есть больше данных чем приходит, не перезаписываем
    if (existing && existing.nodes.length > incoming.nodes.length) {
      return existing;
    }

    return {
      __typename,
      count,
      nodes: incoming.nodes,
    };
  }

  // Если это загрузка следующей страницы (page > 0)
  if (existing) {
    // Создаем Set существующих ID для быстрой проверки
    const existingIds = new Set(getExistingIds(existing.nodes));

    // Фильтруем только новые элементы, которых еще нет в кеше
    const newNodes = incoming.nodes.filter((node) => {
      const nodeId = getNodeId(node);
      return nodeId && !existingIds.has(nodeId);
    });

    const result = {
      __typename,
      count,
      nodes: [...existing.nodes, ...newNodes],
    };

    return result;
  }

  // Если нет существующих данных, просто возвращаем новые
  console.log("connectionPageMerge: returning new data (no existing)");
  return {
    __typename,
    count,
    nodes: incoming.nodes,
  };
};

export default function createApolloCache() {
  return new InMemoryCache({
    typePolicies: {
      BookMetadata: {
        // BookMetadata не имеет ID, поэтому используем merge функцию
        merge(existing, incoming) {
          // Мержим все поля, отдавая приоритет incoming данным
          return { ...existing, ...incoming };
        },
      },
      Query: {
        fields: {
          contents: {
            keyArgs: [
              "productId",
              "productIds",
              "collectionId",
              "collectionIds",
              "collectionTypeIds",
              "collectionTypeIdsOnlyNull",
              "status",
              "type",
              "sort",
              "period",
              "search",
              "relatedContentId",
              "tagIds",
              "anyTagIds",
              "rightholderIds",
              "creatorIds",
              "duration",
              "uploadDate",
            ],
            merge: connectionLimitMerge,
          },
          comments: {
            keyArgs: ["filter", "rowsPerPage"],
            merge: connectionLimitMerge,
          },
          posts: {
            keyArgs: ["tagIds", "rightholderIds"],
            merge: connectionPageMerge,
          },
          products: {
            keyArgs: [
              "rightholderSlug",
              "type",
              "types",
              "rowsPerPage",
              "rightholderIds",
            ],
            merge: connectionPageMerge,
          },
          productPurchases: {
            keyArgs: ["rowsPerPage"],
            merge: connectionPageMerge,
          },
          bans: {
            keyArgs: ["rowsPerPage"],
            merge: connectionPageMerge,
          },
          promocodes: {
            keyArgs: ["rowsPerPage"],
            merge: connectionPageMerge,
          },
        },
      },
    },
  });
}
